/**
 * Demand — slots, day classes and the censoring correction.
 *
 * THE CENSORING PROBLEM (from the original brief, never built until V3):
 * a sold-out 10:00 TRUNCATES observed demand. The people it turned away appear
 * on the 11:30 or nowhere at all. Train the model on raw observations and it
 * learns "demand equals what we could seat" — which is precisely the bias the
 * Studio exists to escape, and it biases AGAINST the case for running more.
 *
 * The fix is standard rail/airline practice: un-truncate using low-load analogue
 * days as the shape prior. We can do it because agg.ResearchSlotDaily gives us
 * per-DATE, per-slot passengers, so the same 10:00 can be compared on days when
 * it was nowhere near its ceiling.
 *
 * Method, per (month x day-class x direction):
 *   1. Per slot, take the observed maximum across running days.
 *   2. A day is UNCAPPED if no slot on it reached CAP_THRESHOLD of that slot's
 *      own maximum — nothing was turning people away.
 *   3. From the uncapped days, derive the demand SHAPE: each slot's share of
 *      the day's total.
 *   4. On a capped day, scale that shape up to the day's own observed total via
 *      the slots that were NOT capped, and read off what the capped slots would
 *      have carried.
 *   5. Take the per-slot mean of max(observed, estimated), bounded by MAX_UPLIFT.
 *
 * Bounded, reversible (a toggle), and every slot reports its uplift on screen.
 * With fewer than MIN_UNCAPPED_DAYS analogue days we decline to correct and say
 * so rather than inventing a prior.
 */
import type {
  ClassDemand, DemandRow, Network, Slot, SlotDailyRow, UntimedRow,
} from './types'
import { routeLabel } from './network'

/**
 * A slot is treated as SUPPRESSED on a day when its share of that day's traffic
 * falls this far below its normal share. That is the flat-top signature of a
 * sell-out: the departure did not grow with the rest of the day because it
 * physically could not.
 */
const SUPPRESSED_SHARE = 0.85
/** Never uplift a slot by more than this — a guard against a runaway prior. */
export const MAX_UPLIFT = 1.6
/** Quiet days needed before we trust a shape prior. */
export const MIN_QUIET_DAYS = 3
/**
 * The unsuppressed slots must account for at least this much of a normal day
 * before we scale their loading up to a whole-day estimate. Below it the
 * extrapolation rests on too little of the day to be worth anything.
 */
const MIN_FREE_SHARE = 0.4

export const toMin = (t: string): number => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim())
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN
}

export const fmtMin = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(Math.round(mins) % 60).padStart(2, '0')}`

/**
 * Parse a MERAC Session label into station indices.
 * 'Altered Autocar Pickering to Grosmont (Limited Seating)' -> { o: 0, d: 3 }
 *
 * This is what makes Mode B possible without new data: the demand is already
 * origin-destination aware, so a Pickering->Whitby block can be split into a
 * main-line leg, a transfer and a shuttle leg.
 */
export function parseRoute(net: Network, label: string | null, direction: string): { o: number; d: number } {
  const last = net.stations.length - 1
  const fallback = direction === 'Northbound' ? { o: 0, d: last } : { o: last, d: 0 }
  if (!label) return fallback
  const clean = label.replace(/\(.*?\)/g, ' ').toLowerCase()
  const parts = clean.split(' to ')
  if (parts.length < 2) return fallback
  const find = (txt: string) => net.stations.findIndex((s) => txt.includes(s.name.toLowerCase()))
  const o = find(parts[0])
  const d = find(parts[parts.length - 1])
  return o >= 0 && d >= 0 && o !== d ? { o, d } : fallback
}

// ---------------------------------------------------------------------------
//  Day classes
// ---------------------------------------------------------------------------

/** V2 grain. */
export const BASIC_CLASSES = ['Sat', 'Sun', 'Wkdy'] as const
/** V3 grain — a July Tuesday and an October Tuesday are not the same animal. */
export const FINE_CLASSES = ['Sat', 'Sun', 'WkdyH', 'WkdyT'] as const

export const CLASS_LABELS: Record<string, string> = {
  Sat: 'Saturday',
  Sun: 'Sunday',
  Wkdy: 'Weekday',
  WkdyH: 'Weekday (school holiday)',
  WkdyT: 'Weekday (term time)',
}

/** Fallback weights when the running-day counts are unavailable. */
export const FALLBACK_WEIGHTS: Record<string, number> = {
  Sat: 1, Sun: 1, Wkdy: 5, WkdyH: 5, WkdyT: 5,
}

/**
 * Days-per-week each class represents, taken from the MEASURED running-day
 * counts for that month where we have them. A month that ran 9 school-holiday
 * weekdays and 3 term weekdays should be weighted that way, not 5 and 5.
 */
export function classWeights(
  untimed: UntimedRow[], yr: number, month: number, classes: readonly string[]
): Record<string, number> {
  const rows = untimed.filter((u) => u.yr === yr && u.month_no === month && classes.includes(u.day_class))
  const days: Record<string, number> = {}
  let total = 0
  for (const c of classes) {
    const n = rows.find((r) => r.day_class === c)?.running_days ?? 0
    days[c] = n
    total += n
  }
  if (total === 0) {
    const out: Record<string, number> = {}
    for (const c of classes) out[c] = FALLBACK_WEIGHTS[c] ?? 1
    return out
  }
  // Scale the month's running days down to a nominal 7-day week so the
  // "per week" figures stay comparable across months of different lengths.
  const weeks = total / 7
  const out: Record<string, number> = {}
  for (const c of classes) out[c] = weeks > 0 ? days[c] / weeks : 0
  return out
}

// ---------------------------------------------------------------------------
//  Slots
// ---------------------------------------------------------------------------

/**
 * The days a slice's standard trains actually ran — the divisor that turns a
 * month's timed sales into passengers per running day. ONE owner: the season
 * build, the live slice, the month chart and the backtest all read it here.
 *
 * It is the longest-running service's days_run, NOT the untimed running-day
 * count: that counts every day the tills took anything (closed Thursdays and
 * Fridays when only the shop sold, gala days on their own timetable), so
 * dividing by it averaged each train over days it never ran. September 2026
 * WkdyT: the 09:20 sold 2,628 on 15 running days — 175 a day, not the 125 the
 * 21 till days gave (Chris, 2026-10-28). Part-month specials are still poured
 * over the whole slice (D5): a 3-day extra divides by 15, not by 3.
 */
export function timedRunningDays(rows: readonly DemandRow[]): number {
  return rows.reduce((m, r) => Math.max(m, r.days_run), 0)
}

/** Demand rows for one (month x day-class x direction) -> engine slots. */
/**
 * @param runningDays The SLICE's running-day count. A service that ran only a
 * few days of the month (a half-term extra, a special) must be spread over the
 * slice's whole day count on the modelled "average day", not divided by its own
 * short run — dividing by its own `days_run` poured it onto every modelled day
 * at full strength, which is exactly the +44% calibration bias of D5 (July's
 * Wkdy slice mixes 3-day and 21-day services). Omitted ⇒ each service's own
 * days_run (the old behaviour, correct only when every service runs every day).
 */
export function mkSlots(net: Network, rows: DemandRow[], dir: string, runningDays?: number): Slot[] {
  return rows
    .filter((r) => r.direction === dir && r.pax > 0 && r.days_run > 0)
    .map((r) => {
      const { o, d } = parseRoute(net, r.service, dir)
      return {
        minutes: toMin(r.dep_time),
        time: r.dep_time,
        paxPerDay: r.pax / (runningDays && runningDays > 0 ? runningDays : r.days_run),
        netPerPax: r.pax > 0 ? r.net / r.pax : 0,
        o,
        d,
        route: routeLabel(net, o, d),
      }
    })
    .filter((s) => Number.isFinite(s.minutes))
    .sort((a, b) => a.minutes - b.minutes)
}

// ---------------------------------------------------------------------------
//  Censoring correction
// ---------------------------------------------------------------------------

export interface CensoringReport {
  applied: boolean
  reason?: string
  /** 'HH:MM|direction' -> uplift factor. */
  uplift: Record<string, number>
  /** Quiet days the shape prior was learned from. */
  uncappedDays: number
  /** Days on which at least one departure looked suppressed. */
  cappedDays: number
  /** Extra average passengers per day the correction added. */
  addedPerDay: number
}

interface DayShape { date: string; total: number; bySlot: Record<string, number> }

function buildDays(rows: SlotDailyRow[]): DayShape[] {
  const byDate = new Map<string, DayShape>()
  for (const r of rows) {
    const cur = byDate.get(r.actual_date) ?? { date: r.actual_date, total: 0, bySlot: {} }
    cur.bySlot[r.dep_time] = (cur.bySlot[r.dep_time] ?? 0) + r.pax
    cur.total += r.pax
    byDate.set(r.actual_date, cur)
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Un-truncate one direction's demand for one (month x day-class).
 * Returns per-slot corrected mean passengers/day plus the uplift applied.
 */
export function correctCensoring(
  slotDaily: SlotDailyRow[], yr: number, month: number, dayClass: string, dir: string
): { corrected: Record<string, number>; report: CensoringReport } {
  const rows = slotDaily.filter(
    (r) => r.yr === yr && r.month_no === month && r.day_class === dayClass && r.direction === dir
  )
  const empty: CensoringReport = { applied: false, uplift: {}, uncappedDays: 0, cappedDays: 0, addedPerDay: 0 }
  if (rows.length === 0) return { corrected: {}, report: { ...empty, reason: 'no per-date demand available' } }

  const days = buildDays(rows)
  const slotTimes = [...new Set(rows.map((r) => r.dep_time))].sort((a, b) => toMin(a) - toMin(b))
  const observedMean: Record<string, number> = {}
  for (const t of slotTimes) {
    observedMean[t] = days.reduce((s, d) => s + (d.bySlot[t] ?? 0), 0) / days.length
  }

  // 1. The shape prior comes from the QUIET half of the month — days in the
  //    lower half of daily totals. Nobody is turned away on a quiet day, so the
  //    pattern across the day is undistorted.
  //
  //    An earlier version instead looked for days where no departure reached 90%
  //    of its own record loading. That could hardly ever find one: the day that
  //    SET a departure's record is at 100% of it by definition, so with ten
  //    departures and a dozen running days almost every day got disqualified and
  //    the correction never ran.
  const totals = days.map((d) => d.total).sort((a, b) => a - b)
  const median = totals[Math.floor(totals.length / 2)] ?? 0
  const quiet = days.filter((d) => d.total <= median)

  if (quiet.length < MIN_QUIET_DAYS) {
    return {
      corrected: observedMean,
      report: {
        ...empty,
        reason: `only ${days.length} running day${days.length === 1 ? '' : 's'} in this month and day type — at least ${MIN_QUIET_DAYS} quiet ones are needed to learn the shape of demand, so it is left uncorrected`,
        uncappedDays: quiet.length,
        cappedDays: 0,
      },
    }
  }

  const quietTotal = quiet.reduce((s, d) => s + d.total, 0)
  const shape: Record<string, number> = {}
  for (const t of slotTimes) {
    const v = quiet.reduce((s, d) => s + (d.bySlot[t] ?? 0), 0)
    shape[t] = quietTotal > 0 ? v / quietTotal : 0
  }

  // 2. A departure is SUPPRESSED on a given day when its share of that day's
  //    traffic sits materially below its normal share — it did not grow with the
  //    rest of the day because it could not. That is what a sell-out looks like
  //    in the data, and the test is not circular: the shape comes only from days
  //    the slot in question was not straining on.
  const isSuppressed = (d: DayShape, t: string): boolean => {
    if (d.total <= 0 || shape[t] <= 0) return false
    const share = (d.bySlot[t] ?? 0) / d.total
    return share < shape[t] * SUPPRESSED_SHARE
  }

  // 3. On each day, scale the unsuppressed departures up to an implied whole-day
  //    demand, then read off what the suppressed ones would have carried. This is
  //    what actually ADDS demand: the anchor is the part of the day that was not
  //    binding, not the observed total.
  const estSum: Record<string, number> = {}
  for (const t of slotTimes) estSum[t] = 0
  let suppressedDays = 0
  for (const d of days) {
    const suppressed = slotTimes.filter((t) => isSuppressed(d, t))
    if (suppressed.length > 0) suppressedDays += 1
    const free = slotTimes.filter((t) => !suppressed.includes(t))
    const freeShare = free.reduce((s, t) => s + shape[t], 0)
    const freeObserved = free.reduce((s, t) => s + (d.bySlot[t] ?? 0), 0)
    const canScale = suppressed.length > 0 && freeShare >= MIN_FREE_SHARE
    const impliedTotal = canScale ? freeObserved / freeShare : d.total
    for (const t of slotTimes) {
      const obs = d.bySlot[t] ?? 0
      if (!canScale || !suppressed.includes(t)) { estSum[t] += obs; continue }
      // Never revise a departure DOWN, and never past the uplift bound.
      estSum[t] += Math.min(Math.max(obs, impliedTotal * shape[t]), obs * MAX_UPLIFT)
    }
  }

  const corrected: Record<string, number> = {}
  const uplift: Record<string, number> = {}
  let added = 0
  for (const t of slotTimes) {
    corrected[t] = estSum[t] / days.length
    const u = observedMean[t] > 0 ? corrected[t] / observedMean[t] : 1
    if (u > 1.001) uplift[`${t}|${dir}`] = u
    added += corrected[t] - observedMean[t]
  }

  return {
    corrected,
    report: {
      applied: Object.keys(uplift).length > 0,
      reason: Object.keys(uplift).length === 0
        ? `no departure looked suppressed: across ${days.length} running days every one grew in step with the rest of its day, so nothing appears to have been turning people away`
        : undefined,
      uplift,
      uncappedDays: quiet.length,
      cappedDays: suppressedDays,
      addedPerDay: added,
    },
  }
}

/** Apply a censoring correction to a slot list, tagging each slot's uplift. */
export function applyCorrection(slots: Slot[], corrected: Record<string, number>): Slot[] {
  return slots.map((s) => {
    const c = corrected[s.time]
    if (c == null || !(c > s.paxPerDay)) return s
    return { ...s, paxPerDay: c, uplift: s.paxPerDay > 0 ? c / s.paxPerDay : 1 }
  })
}

/** Build a corrected ClassDemand for one day class, with a combined report. */
export function buildClassDemand(
  net: Network,
  rows: DemandRow[],
  slotDaily: SlotDailyRow[] | undefined,
  cls: string,
  weight: number,
  yr: number,
  month: number,
  correct: boolean,
  /** The slice's running-day count — see mkSlots (the D5 fix). */
  runningDays?: number
): { demand: ClassDemand; reports: Record<string, CensoringReport> } {
  let north = mkSlots(net, rows, 'Northbound', runningDays)
  let south = mkSlots(net, rows, 'Southbound', runningDays)
  const reports: Record<string, CensoringReport> = {}
  if (correct && slotDaily && slotDaily.length > 0) {
    const n = correctCensoring(slotDaily, yr, month, cls, 'Northbound')
    const s = correctCensoring(slotDaily, yr, month, cls, 'Southbound')
    north = applyCorrection(north, n.corrected)
    south = applyCorrection(south, s.corrected)
    reports[`${cls}|Northbound`] = n.report
    reports[`${cls}|Southbound`] = s.report
  }
  return { demand: { cls, weight, north, south }, reports }
}

export const totalWeight = (classes: ClassDemand[]): number =>
  classes.reduce((t, c) => t + c.weight, 0)
